import React, { useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ArrowBack,
  CheckCircle,
  ContentCopy,
  Explore,
  Link as LinkIcon,
  Lock,
  Map as MapIcon,
  PersonAdd,
  Public,
  RadioButtonUnchecked,
  Search,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { parseISO, isValid, format } from 'date-fns';
import AppNavigation from './AppNavigation';
import DiveSiteMap from './DiveSiteMap';
import { useNotification } from './NotificationContext';
import { useDiveSiteCatalog } from '../hooks/useDiveSiteCatalog';
import { diveSiteListService, DiveSiteListApiError } from '../services/diveSiteListService';
import { scheduledDivesService } from '../services/scheduledDivesService';
import { authService, UserLookupResult } from '../services/authService';
import type { DiveSiteList, DiveSiteListVisibility } from '../types/DiveSite';

const formatListVisibility = (visibility: DiveSiteListVisibility): string => {
  if (visibility === 'invite-only') return 'Invite only';
  if (visibility === 'link') return 'Shared by link';
  return 'Private';
};

const DiveSiteListView: React.FC = () => {
  const navigate = useNavigate();
  const { showError, showSuccess } = useNotification();
  const { shareCode, listId } = useParams<{ shareCode?: string; listId?: string }>();
  const { allSites, loading: catalogLoading, error: catalogError } = useDiveSiteCatalog();
  const [list, setList] = React.useState<DiveSiteList | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [errorStatus, setErrorStatus] = React.useState<number | null>(null);
  const [resolvedCollaborators, setResolvedCollaborators] = React.useState<UserLookupResult[]>([]);
  const [savingCheckIn, setSavingCheckIn] = React.useState<string | null>(null);
  const [savingSchedule, setSavingSchedule] = React.useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Invite collaborator dialog
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteUserId, setInviteUserId] = useState('');
  const [inviteSaving, setInviteSaving] = useState(false);

  const currentUser = authService.getUser();
  const currentUserId = currentUser?.userId;
  const isSharedView = Boolean(shareCode);
  const isAuthenticated = authService.isAuthenticated();

  const isOwnerOrCollaborator = useMemo(() => {
    if (!list || !currentUserId || !isAuthenticated) return false;
    return list.ownerUserId === currentUserId || list.collaboratorUserIds.includes(currentUserId);
  }, [list, currentUserId, isAuthenticated]);

  const isOwner = useMemo(() => {
    if (!list || !currentUserId) return false;
    return list.ownerUserId === currentUserId;
  }, [list, currentUserId]);

  React.useEffect(() => {
    const loadList = async () => {
      if (shareCode) {
        try {
          setLoading(true);
          setError(null);
          setErrorStatus(null);
          const nextList = await diveSiteListService.getSharedDiveSiteList(shareCode);
          setList(nextList);
        } catch (err) {
          if (err instanceof DiveSiteListApiError) setErrorStatus(err.status);
          setError(err instanceof Error ? err.message : 'Unable to load shared dive site list.');
        } finally {
          setLoading(false);
        }
        return;
      }

      if (listId) {
        if (!isAuthenticated) {
          setError('Sign in to view collaborator-only dive-site lists.');
          setErrorStatus(401);
          setLoading(false);
          return;
        }

        try {
          setLoading(true);
          setError(null);
          setErrorStatus(null);
          const nextList = await diveSiteListService.getDiveSiteList(listId);
          setList(nextList);
        } catch (err) {
          if (err instanceof DiveSiteListApiError) setErrorStatus(err.status);
          setError(err instanceof Error ? err.message : 'Unable to load dive site list.');
        } finally {
          setLoading(false);
        }
        return;
      }

      setError('No dive-site list was specified.');
      setErrorStatus(400);
      setLoading(false);
    };

    loadList();
  }, [listId, shareCode, isAuthenticated]);

  React.useEffect(() => {
    if (!list || !isAuthenticated || list.collaboratorUserIds.length === 0) {
      setResolvedCollaborators([]);
      return;
    }

    let isCancelled = false;
    const loadCollaborators = async () => {
      const users = await authService.getUsersByIds(list.collaboratorUserIds);
      if (!isCancelled) setResolvedCollaborators(users);
    };

    loadCollaborators();
    return () => { isCancelled = true; };
  }, [list, isAuthenticated]);

  const visibleSites = useMemo(() => {
    if (!list) return [];
    const siteIds = new Set(list.siteIds);
    return allSites.filter((site) => siteIds.has(site.id) || (site.siteId && siteIds.has(site.siteId)));
  }, [allSites, list]);

  const filteredSites = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return visibleSites;
    return visibleSites.filter(
      (s) =>
        s.name.toLowerCase().includes(q) ||
        s.region.toLowerCase().includes(q) ||
        s.country.toLowerCase().includes(q) ||
        s.tags.some((t) => t.toLowerCase().includes(q))
    );
  }, [visibleSites, searchQuery]);

  const resolvedCollaboratorMap = useMemo(
    () => new Map(resolvedCollaborators.map((user) => [user.userId, user])),
    [resolvedCollaborators]
  );

  const formatUserLabel = (user?: UserLookupResult | null, fallbackUserId?: string): string => {
    if (!user) return fallbackUserId ?? 'Unknown diver';
    const fullName = `${user.firstName} ${user.lastName}`.trim();
    return fullName || user.email || fallbackUserId || 'Unknown diver';
  };

  const getSiteId = (site: { id: string; siteId?: string }, listSiteIds: string[]): string => {
    if (listSiteIds.includes(site.id)) return site.id;
    if (site.siteId && listSiteIds.includes(site.siteId)) return site.siteId;
    return site.id;
  };

  const handleCheckIn = async (canonicalSiteId: string, currentlyDived: boolean) => {
    if (!list || !currentUserId) return;
    setSavingCheckIn(canonicalSiteId);
    try {
      const updated = await diveSiteListService.checkIn(list.listId, {
        siteId: canonicalSiteId,
        checkedIn: !currentlyDived,
      });
      setList(updated);
      showSuccess(!currentlyDived ? 'Marked as dived!' : 'Check-in removed.');
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to update check-in.');
    } finally {
      setSavingCheckIn(null);
    }
  };

  const handleSetSchedule = async (canonicalSiteId: string, dateValue: string) => {
    if (!list) return;
    setSavingSchedule(canonicalSiteId);
    try {
      const updatedSchedules = { ...(list.siteSchedules ?? {}), [canonicalSiteId]: dateValue || '' };
      if (!dateValue) delete updatedSchedules[canonicalSiteId];

      const updated = await diveSiteListService.updateDiveSiteList({
        listId: list.listId,
        name: list.name,
        description: list.description,
        visibility: list.visibility as DiveSiteListVisibility,
        siteIds: list.siteIds,
        collaboratorUserIds: list.collaboratorUserIds,
        siteSchedules: updatedSchedules,
      });
      setList(updated);

      // If a date is set, also create a ScheduledDive to appear on the calendar
      if (dateValue) {
        const siteName = visibleSites.find((s) => s.id === canonicalSiteId || s.siteId === canonicalSiteId)?.name;
        try {
          await scheduledDivesService.createScheduledDive({
            scheduledDate: dateValue,
            siteName: siteName ?? list.name,
            notes: `From dive list: ${list.name}`,
          });
          showSuccess('Date saved and added to calendar.');
        } catch {
          showSuccess('Date saved.');
        }
      } else {
        showSuccess('Schedule date cleared.');
      }
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to update schedule.');
    } finally {
      setSavingSchedule(null);
    }
  };

  const handleInviteCollaborator = async () => {
    if (!list || !inviteUserId.trim()) return;
    setInviteSaving(true);
    try {
      const updated = await diveSiteListService.updateDiveSiteList({
        listId: list.listId,
        name: list.name,
        description: list.description,
        visibility: list.visibility as DiveSiteListVisibility,
        siteIds: list.siteIds,
        collaboratorUserIds: [...list.collaboratorUserIds, inviteUserId.trim()],
        siteSchedules: list.siteSchedules,
      });
      setList(updated);
      setInviteUserId('');
      setInviteOpen(false);
      showSuccess('Collaborator added.');
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to add collaborator.');
    } finally {
      setInviteSaving(false);
    }
  };

  const handleRemoveCollaborator = async (collaboratorId: string) => {
    if (!list) return;
    try {
      const updated = await diveSiteListService.updateDiveSiteList({
        listId: list.listId,
        name: list.name,
        description: list.description,
        visibility: list.visibility as DiveSiteListVisibility,
        siteIds: list.siteIds,
        collaboratorUserIds: list.collaboratorUserIds.filter((id) => id !== collaboratorId),
        siteSchedules: list.siteSchedules,
      });
      setList(updated);
      showSuccess('Collaborator removed.');
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to remove collaborator.');
    }
  };

  const handleCopyUserId = async () => {
    if (!currentUserId) { showError('No signed-in user ID is available to copy.'); return; }
    try {
      await navigator.clipboard.writeText(currentUserId);
      showSuccess('Your user ID has been copied to the clipboard.');
    } catch {
      showError(`Copy failed. Your user ID is ${currentUserId}.`);
    }
  };

  const handleCopyAccessRequest = async () => {
    if (!currentUserId) { showError('Sign in before copying an access request.'); return; }
    const displayName = currentUser ? `${currentUser.firstName} ${currentUser.lastName}`.trim() || currentUser.email : 'New Achievement Scuba diver';
    const requestMessage = [
      'Hi, please add me as a collaborator on this dive-site list.',
      `Name: ${displayName}`,
      currentUser?.email ? `Email: ${currentUser.email}` : null,
      `User ID: ${currentUserId}`,
      `List URL: ${window.location.href}`,
    ].filter(Boolean).join('\n');
    try {
      await navigator.clipboard.writeText(requestMessage);
      showSuccess('Access request details copied to the clipboard.');
    } catch {
      showError('Copy failed. Please share your user ID with the list owner manually.');
    }
  };

  const missingSiteCount = Math.max((list?.siteIds.length ?? 0) - visibleSites.length, 0);

  if (loading || catalogLoading) {
    return (
      <Box>
        <AppNavigation />
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 2 }}>
          <CircularProgress size={20} />
          <Typography variant="body1">Loading dive-site list…</Typography>
        </Stack>
      </Box>
    );
  }

  if (error || !list) {
    return (
      <Box>
        <AppNavigation />
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/dive-lists')} sx={{ mb: 2 }}>
          Back to dive lists
        </Button>
        {errorStatus === 403 ? (
          <Stack spacing={2}>
            <Alert severity="warning">You are signed in, but this collaborator-only dive-site list has not been shared with your account yet.</Alert>
            <Alert severity="info">Ask the list owner to add your user ID to the collaborator list.{currentUserId ? ` Your user ID is ${currentUserId}.` : ''}</Alert>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              {currentUserId && <Button variant="contained" startIcon={<ContentCopy />} onClick={handleCopyUserId}>Copy my user ID</Button>}
              {currentUserId && <Button variant="outlined" startIcon={<ContentCopy />} onClick={handleCopyAccessRequest}>Copy access request</Button>}
              <Button variant="contained" onClick={() => navigate('/dive-sites')}>Browse dive sites</Button>
              <Button variant="outlined" onClick={() => navigate('/')}>Go home</Button>
            </Stack>
          </Stack>
        ) : errorStatus === 401 ? (
          <Stack spacing={2}>
            <Alert severity="warning">{error || 'Sign in to view this dive-site list.'}</Alert>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              <Button variant="contained" onClick={() => navigate('/')}>Go home</Button>
              <Button variant="outlined" onClick={() => navigate('/dive-sites')}>Browse dive sites</Button>
            </Stack>
          </Stack>
        ) : (
          <Alert severity="warning">{error || 'That dive-site list could not be found.'}</Alert>
        )}
      </Box>
    );
  }

  return (
    <Box>
      <AppNavigation />

      <Button startIcon={<ArrowBack />} onClick={() => navigate('/dive-lists')} sx={{ mb: 2 }}>
        Back to dive lists
      </Button>

      <Stack spacing={2.5}>
        {/* Header */}
        <Box>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
            <Box>
              <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap" sx={{ mb: 1 }}>
                <Typography variant="h4" color="primary" sx={{ fontWeight: 700 }}>
                  {list.name}
                </Typography>
                <Chip icon={<Public />} label={formatListVisibility(list.visibility as DiveSiteListVisibility)} variant="outlined" />
                {isSharedView && list.shareCode ? <Chip icon={<LinkIcon />} label={`Code ${list.shareCode}`} variant="outlined" /> : null}
                {!isSharedView ? <Chip icon={<Lock />} label="Authenticated view" variant="outlined" /> : null}
              </Stack>
              <Typography variant="body1" color="text.secondary">
                By {list.ownerDisplayName || 'New Achievement Scuba diver'}
              </Typography>
              {list.description && (
                <Typography variant="body1" color="text.secondary" sx={{ mt: 0.75 }}>
                  {list.description}
                </Typography>
              )}
            </Box>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              <Chip label={`${visibleSites.length} site${visibleSites.length !== 1 ? 's' : ''}`} variant="outlined" />
              {list.collaboratorUserIds.length > 0 && <Chip label={`${list.collaboratorUserIds.length} diver${list.collaboratorUserIds.length !== 1 ? 's' : ''}`} variant="outlined" />}
              {isOwner && list.visibility !== 'private' && (
                <Tooltip title="Add collaborator">
                  <Button size="small" variant="outlined" startIcon={<PersonAdd />} onClick={() => setInviteOpen(true)}>
                    Invite
                  </Button>
                </Tooltip>
              )}
            </Stack>
          </Stack>
        </Box>

        {!isSharedView && <Alert severity="info">This is the authenticated view — owners and invited collaborators can update sites, schedules, and check-ins.</Alert>}
        {catalogError && <Alert severity="warning">{catalogError}</Alert>}
        {missingSiteCount > 0 && (
          <Alert severity="info">
            {missingSiteCount} site{missingSiteCount === 1 ? '' : 's'} in this list could not be matched to the current catalog snapshot yet.
          </Alert>
        )}

        {/* Collaborators panel */}
        {!isSharedView && list.collaboratorUserIds.length > 0 && (
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>Collaborators</Typography>
            <Stack spacing={1}>
              {list.collaboratorUserIds.map((collaboratorUserId) => {
                const collaborator = resolvedCollaboratorMap.get(collaboratorUserId);
                const isCurrentUser = collaboratorUserId === currentUserId;
                return (
                  <Paper key={collaboratorUserId} variant="outlined" sx={{ p: 1.25 }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }}>
                      <Box>
                        <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{formatUserLabel(collaborator, collaboratorUserId)}</Typography>
                          {isCurrentUser && <Chip size="small" color="primary" label="You" />}
                        </Stack>
                        {collaborator?.email && <Typography variant="body2" color="text.secondary">{collaborator.email}</Typography>}
                        <Typography variant="caption" color="text.secondary">{collaboratorUserId}</Typography>
                      </Box>
                      {isOwner && (
                        <Button size="small" color="error" variant="outlined" onClick={() => handleRemoveCollaborator(collaboratorUserId)}>
                          Remove
                        </Button>
                      )}
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>
          </Box>
        )}

        {/* Map */}
        <Box>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
            <MapIcon color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {isSharedView ? 'Shared map' : 'List map'}
            </Typography>
          </Stack>
          <DiveSiteMap
            sites={visibleSites}
            siteMatches={new Map()}
            preferences={{ favoriteSiteIds: [], wishlistSiteIds: [], customLists: [], ignoredDiveSiteMatches: {} }}
            height={360}
            dialogTitle={list.name}
          />
        </Box>

        {/* Search */}
        {visibleSites.length > 0 && (
          <TextField
            fullWidth
            size="small"
            placeholder="Search sites in this list…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        )}

        {filteredSites.length === 0 && visibleSites.length > 0 && (
          <Alert severity="info">No sites match your search.</Alert>
        )}

        {visibleSites.length === 0 && (
          <Alert severity="info">This dive-site list does not contain any currently visible catalog sites.</Alert>
        )}

        {/* Site cards */}
        <Grid container spacing={2}>
          {filteredSites.map((site) => {
            const canonicalSiteId = getSiteId(site, list.siteIds);
            const divers = list.divedByUserIds?.[canonicalSiteId] ?? [];
            const hasDived = Boolean(currentUserId && divers.includes(currentUserId));
            const scheduledDate = list.siteSchedules?.[canonicalSiteId] ?? '';
            const checkInPending = savingCheckIn === canonicalSiteId;
            const schedulePending = savingSchedule === canonicalSiteId;

            return (
              <Grid item xs={12} md={6} lg={4} key={site.id}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, flexGrow: 1 }}>
                    <Box>
                      <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          {site.name}
                        </Typography>
                        {isAuthenticated && isOwnerOrCollaborator && (
                          <Tooltip title={hasDived ? 'Mark as not dived' : 'Mark as dived'}>
                            <span>
                              <IconButton
                                size="small"
                                color={hasDived ? 'success' : 'default'}
                                onClick={() => handleCheckIn(canonicalSiteId, hasDived)}
                                disabled={checkInPending}
                              >
                                {checkInPending ? <CircularProgress size={18} /> : hasDived ? <CheckCircle /> : <RadioButtonUnchecked />}
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </Stack>
                      <Typography variant="body2" color="text.secondary">
                        {site.region}, {site.country}
                      </Typography>
                    </Box>

                    <Typography variant="body2" color="text.secondary">
                      {site.summary}
                    </Typography>

                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                      <Chip size="small" icon={<Explore />} label={site.siteType.replace('-', ' ')} />
                      <Chip size="small" label={site.difficulty} />
                      {site.tags.slice(0, 3).map((tag) => (
                        <Chip key={tag} size="small" variant="outlined" label={tag} />
                      ))}
                    </Stack>

                    {/* Who has dived this site */}
                    {divers.length > 0 && (
                      <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" alignItems="center">
                        <Typography variant="caption" color="text.secondary">Dived by:</Typography>
                        {divers.map((uid) => {
                          const user = resolvedCollaboratorMap.get(uid);
                          const label = user ? ((`${user.firstName} ${user.lastName}`.trim()) || user.email || uid) : uid === currentUserId ? 'You' : uid.slice(0, 8);
                          const isMe = uid === currentUserId;
                          return (
                            <Chip
                              key={uid}
                              size="small"
                              color={isMe ? 'success' : 'default'}
                              label={label}
                            />
                          );
                        })}
                      </Stack>
                    )}

                    {/* Scheduled date */}
                    {isAuthenticated && isOwnerOrCollaborator && (
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          Planned date
                        </Typography>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <DatePicker
                              value={scheduledDate && isValid(parseISO(scheduledDate)) ? parseISO(scheduledDate) : null}
                              onChange={(date) => {
                                const formatted = date && isValid(date) ? format(date, 'yyyy-MM-dd') : '';
                                handleSetSchedule(canonicalSiteId, formatted);
                              }}
                              disabled={schedulePending}
                              slotProps={{
                                textField: {
                                  size: 'small',
                                  sx: { maxWidth: 200 },
                                  placeholder: 'MM/DD/YYYY',
                                },
                              }}
                            />
                            {schedulePending && <CircularProgress size={18} />}
                            {scheduledDate && !schedulePending && (
                              <Tooltip title="Clear date">
                                <Button size="small" onClick={() => handleSetSchedule(canonicalSiteId, '')}>Clear</Button>
                              </Tooltip>
                            )}
                          </Stack>
                        </LocalizationProvider>
                      </Box>
                    )}

                    {/* Read-only schedule for non-collaborators */}
                    {(!isAuthenticated || !isOwnerOrCollaborator) && scheduledDate && (
                      <Chip size="small" color="secondary" label={`Planned: ${scheduledDate}`} />
                    )}

                    <Typography variant="body2" color="text.secondary">
                      {site.highlights.join(' • ')}
                    </Typography>
                  </CardContent>
                  <CardActions sx={{ px: 2, pb: 2, pt: 0 }}>
                    <Button component={RouterLink} to={`/dive-sites/${site.slug}`} size="small">
                      View details
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Stack>

      {/* Invite collaborator dialog */}
      <Dialog open={inviteOpen} onClose={() => setInviteOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Invite collaborator</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="info">
              Ask the diver to share their user ID with you. They can copy it from their account settings or from the shared list page.
            </Alert>
            <TextField
              label="User ID"
              fullWidth
              value={inviteUserId}
              onChange={(e) => setInviteUserId(e.target.value)}
              placeholder="e.g. usr-abc123…"
            />
            <FormControlLabel
              control={<Checkbox checked={list.visibility !== 'private'} disabled />}
              label={list.visibility === 'private' ? 'Note: list is private — invited divers cannot access it until you change visibility to Invite only or Shared by link.' : 'Invited divers will have full access to add/update sites and schedules.'}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteOpen(false)} disabled={inviteSaving}>Cancel</Button>
          <Button variant="contained" onClick={handleInviteCollaborator} disabled={inviteSaving || !inviteUserId.trim()}>
            {inviteSaving ? <CircularProgress size={18} /> : 'Add collaborator'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DiveSiteListView;
